import { Link } from 'react-router-dom';
import type { Asset, AssetFolder, AssetReview, Project, ProjectFolder } from '@vidmakr/bindings/types';
import { Clapperboard, Folder, FolderOpen, Layers } from 'lucide-react';
import { aspectLabel, averageBitrate, formatBitrate, formatDuration, latestBy, subtreeIds } from '@vidmakr/shared';
import type { useSpaces } from '@/lib/spaces';
import type { MediaIndex } from '@/lib/library';
import { useMediaUrls } from '@/lib/media';
import { useLinkedOrigin } from '@/lib/sources';
import { formatBytes, formatDate, timeAgo } from '@/lib/format';
import { plural } from '@/lib/selection';
import { AssetThumb, MediaImg } from './AssetThumb';
import { InfoRow, InfoSection, InfoTitle } from './Browse';
import { Badge } from './ui';

type Spaces = ReturnType<typeof useSpaces>;

export const REVIEW_LABEL: Record<string, { label: string; tone: 'neutral' | 'accent' | 'ok' | 'warn' }> = {
  in_review: { label: 'In review', tone: 'accent' },
  approved: { label: 'Approved', tone: 'ok' },
  changes_requested: { label: 'Changes requested', tone: 'warn' },
};

/** A project's poster (its first visual clip), or a placeholder. */
export function ProjectThumb({ project, assets, className = 'rounded-t-2xl', duration = true }: { project: Project; assets: Map<string, Asset>; className?: string; duration?: boolean }) {
  const asset = assets.get(project.posterAssetId);
  const urls = useMediaUrls(asset?.posterKey ? asset.id : null, asset?.posterKey);
  return (
    <div className={`relative aspect-video overflow-hidden bg-gradient-to-br from-panel-3 to-bg ${className}`}>
      {urls.poster ? (
        <MediaImg src={urls.poster} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full items-center justify-center text-faint">
          <Clapperboard size={duration ? 28 : 16} />
        </div>
      )}
      {duration ? (
        <div className="absolute bottom-2 right-2 rounded-md bg-black/70 px-1.5 py-0.5 font-mono text-[11px] text-white">{formatDuration(project.durationUs)}</div>
      ) : null}
    </div>
  );
}

const micros = (t: { microsSinceUnixEpoch: bigint }) => t.microsSinceUnixEpoch;

/** "Restricted", inherited restriction, or open to the organization. */
function accessText(restricted: boolean, scopeId: string, grants: number) {
  if (restricted) return grants ? `Restricted · ${grants} ${grants === 1 ? 'person or group' : 'people or groups'}` : 'Restricted · admins only';
  if (scopeId) return 'Restricted (by a space or folder above)';
  return 'Everyone in the organization';
}

function When({ at, by }: { at?: { microsSinceUnixEpoch: bigint } | null; by?: string }) {
  if (!at) return null;
  return (
    <span title={formatDate(at as never)}>
      {timeAgo(at as never)}
      {by ? ` · ${by}` : ''}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Project browser
// ---------------------------------------------------------------------------

/** What a set of projects adds up to: count, media size, newest edit. */
function projectTotals(list: readonly Project[], index: MediaIndex) {
  const media = new Set<string>();
  for (const p of list) for (const id of index.mediaOf(p)) media.add(id);
  return {
    active: list.filter((p) => !p.archived).length,
    archived: list.filter((p) => p.archived).length,
    media: media.size,
    size: index.sizeOf(media),
    storage: index.storageOf(media),
    latest: latestBy(list, (p) => micros(p.updatedAt)),
    durationUs: list.reduce((s, p) => s + p.durationUs, 0),
    ids: media,
  };
}

function TotalsRows({ t, extraMedia }: { t: ReturnType<typeof projectTotals>; extraMedia?: { label: string; ids: string[]; index: MediaIndex } }) {
  const all = new Set([...t.ids, ...(extraMedia?.ids ?? [])]);
  return (
    <>
      <InfoSection title="Contents">
        <InfoRow label="Projects">
          {t.active}
          {t.archived ? ` (+${t.archived} archived)` : ''}
        </InfoRow>
        <InfoRow label="Media used">{t.media ? plural(t.media, 'file') : '0 files'}</InfoRow>
        {extraMedia ? <InfoRow label={extraMedia.label}>{plural(extraMedia.ids.length, 'file')}</InfoRow> : null}
        <InfoRow label="Total size">{extraMedia ? formatBytes(extraMedia.index.sizeOf(all)) : formatBytes(t.size)}</InfoRow>
        <InfoRow label="With proxies">{extraMedia ? formatBytes(extraMedia.index.storageOf(all)) : formatBytes(t.storage)}</InfoRow>
      </InfoSection>
      <InfoSection title="Activity">
        <InfoRow label="Last edited">{t.latest ? <When at={t.latest.updatedAt} by={t.latest.updatedByName} /> : null}</InfoRow>
        {t.latest ? <InfoRow label="In">{t.latest.name}</InfoRow> : null}
      </InfoSection>
    </>
  );
}

/**
 * Details of the project browser's selection, or of the place being browsed
 * (folder, space, or the organization) when nothing is selected.
 */
export function ProjectsInspector({
  selected,
  projects,
  projectById,
  sp,
  index,
  inSpace,
  spaceId,
  folderId,
  orgName,
}: {
  selected: string[];
  projects: readonly Project[];
  projectById: Map<string, Project>;
  sp: Spaces;
  index: MediaIndex;
  inSpace: boolean;
  spaceId: string;
  folderId: string;
  orgName: string;
}) {
  const grantsOf = (id: string) => sp.grants.filter((g) => g.targetId === id).length;
  const inFolders = (ids: Set<string>) => projects.filter((p) => ids.has(p.folderId));
  const folderTree = (f: ProjectFolder) => subtreeIds(sp.folders, f.id);
  const where = (spId: string, fId: string) => [sp.spaceById.get(spId)?.name ?? 'General', ...sp.folderPath(fId).map((f) => f.name)].join(' / ');

  if (selected.length > 1) {
    const ps = selected.filter((k) => k.startsWith('p:')).map((k) => projectById.get(k.slice(2))).filter((p): p is Project => !!p);
    const fs = selected.filter((k) => k.startsWith('f:')).map((k) => sp.folderById.get(k.slice(2))).filter((f): f is ProjectFolder => !!f);
    const inside = fs.flatMap((f) => inFolders(folderTree(f)));
    const all = [...new Map([...ps, ...inside].map((p) => [p.id, p])).values()];
    const t = projectTotals(all, index);
    return (
      <div data-inspector-multi>
        <InfoTitle icon={<Layers size={18} className="text-accent-2" />} title={`${plural(selected.length, 'item')} selected`} subtitle={[ps.length ? plural(ps.length, 'project') : '', fs.length ? plural(fs.length, 'folder') : ''].filter(Boolean).join(', ')} />
        <InfoSection title="Together">
          <InfoRow label="Projects (incl. in folders)">{all.length}</InfoRow>
          <InfoRow label="Total length">{formatDuration(t.durationUs)}</InfoRow>
          <InfoRow label="Media used">{plural(t.media, 'file')}</InfoRow>
          <InfoRow label="Total size">{formatBytes(t.size)}</InfoRow>
          <InfoRow label="With proxies">{formatBytes(t.storage)}</InfoRow>
        </InfoSection>
      </div>
    );
  }

  const key = selected[0];
  const project = key?.startsWith('p:') ? projectById.get(key.slice(2)) : undefined;
  if (project) {
    const media = index.mediaOf(project);
    const review = REVIEW_LABEL[project.reviewStatus];
    return (
      <div data-inspector-project={project.id}>
        <ProjectThumb project={project} assets={index.byId} className="mb-3 rounded-lg" />
        <InfoTitle title={project.name} subtitle={where(project.spaceId, project.folderId)} />
        <div className="mb-3 flex flex-wrap gap-1.5">
          {review ? <Badge tone={review.tone}>{review.label}</Badge> : null}
          {project.template ? <Badge tone="accent">Template</Badge> : null}
          {project.archived ? <Badge>Archived</Badge> : null}
        </div>
        <InfoSection title="Format">
          <InfoRow label="Frame size">
            {project.width}×{project.height} ({aspectLabel(project.width, project.height)})
          </InfoRow>
          <InfoRow label="Frame rate">{project.fps} fps</InfoRow>
          <InfoRow label="Length">{formatDuration(project.durationUs)}</InfoRow>
        </InfoSection>
        <InfoSection title="Media">
          <InfoRow label="Files used">{plural(media.length, 'file')}</InfoRow>
          <InfoRow label="Total size">{formatBytes(index.sizeOf(media))}</InfoRow>
          <InfoRow label="With proxies">{formatBytes(index.storageOf(media))}</InfoRow>
        </InfoSection>
        <InfoSection title="History">
          <InfoRow label="Created">
            <When at={project.createdAt} by={project.createdByName} />
          </InfoRow>
          <InfoRow label="Last edited">
            <When at={project.updatedAt} by={project.updatedByName} />
          </InfoRow>
          {project.reviewRequestedByName && project.reviewStatus !== 'editing' ? <InfoRow label="Review asked by">{project.reviewRequestedByName}</InfoRow> : null}
        </InfoSection>
        <InfoSection title="Access">
          <InfoRow label="Who can open it">{accessText(false, project.scopeId, 0)}</InfoRow>
        </InfoSection>
        <Link to={`/p/${project.id}`} className="mt-1 inline-flex text-xs font-medium text-accent-2 hover:underline">
          Open in the editor
        </Link>
      </div>
    );
  }

  const folder = key?.startsWith('f:') ? sp.folderById.get(key.slice(2)) : !key && folderId ? sp.folderById.get(folderId) : undefined;
  if (folder) {
    const tree = folderTree(folder);
    const t = projectTotals(inFolders(tree), index);
    return (
      <div data-inspector-folder={folder.id}>
        <InfoTitle icon={<Folder size={18} className="text-accent-2" />} title={folder.name} subtitle={`Folder in ${where(folder.spaceId, folder.parentId)}`} />
        <TotalsRows t={t} />
        <InfoSection>
          <InfoRow label="Subfolders">{tree.size - 1}</InfoRow>
          <InfoRow label="Created">
            <When at={folder.createdAt} by={folder.createdByName} />
          </InfoRow>
          <InfoRow label="Access">{accessText(folder.restricted, folder.scopeId, grantsOf(folder.id))}</InfoRow>
        </InfoSection>
      </div>
    );
  }

  if (inSpace) {
    const space = spaceId ? sp.spaceById.get(spaceId) : undefined;
    const list = projects.filter((p) => p.spaceId === spaceId);
    const library = [...index.byId.values()].filter((a) => !a.projectId && a.spaceId === spaceId).map((a) => a.id);
    const t = projectTotals(list, index);
    return (
      <div data-inspector-space={spaceId || 'general'}>
        <InfoTitle
          icon={<span className="mt-0.5 block h-3 w-3 rounded-full" style={{ background: space?.color ?? '#64748b' }} />}
          title={space?.name ?? 'General'}
          subtitle={space?.description || 'Space'}
        />
        <TotalsRows t={t} extraMedia={{ label: spaceId ? 'Space library' : 'Shared library', ids: library, index }} />
        <InfoSection>
          <InfoRow label="Folders">{sp.folders.filter((f) => f.spaceId === spaceId).length}</InfoRow>
          <InfoRow label="Created">{space ? <When at={space.createdAt} by={space.createdByName} /> : null}</InfoRow>
          <InfoRow label="Access">{space ? accessText(space.restricted, space.scopeId, grantsOf(space.id)) : 'Everyone in the organization'}</InfoRow>
        </InfoSection>
      </div>
    );
  }

  const t = projectTotals(projects, index);
  return (
    <div data-inspector-org>
      <InfoTitle icon={<FolderOpen size={18} className="text-accent-2" />} title={orgName} subtitle="All projects you can see" />
      <TotalsRows t={t} />
      <InfoSection>
        <InfoRow label="Spaces">{sp.spaces.length}</InfoRow>
        <InfoRow label="Folders">{sp.folders.length}</InfoRow>
      </InfoSection>
      <p className="mt-2 text-xs text-faint">Select a project or folder to see its details.</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Asset library
// ---------------------------------------------------------------------------

const KIND_LABEL: Record<string, string> = { video: 'Video', audio: 'Audio', image: 'Image', font: 'Font', lut: 'LUT' };
export const kindLabel = (a: { kind: string; ext: string }) => `${KIND_LABEL[a.kind] ?? a.kind}${a.ext ? ` · ${a.ext.toUpperCase()}` : ''}`;

/** What a media folder holds, recursively. */
export interface FolderStats {
  files: number;
  folders: number;
  size: number;
  storage: number;
  latest?: Asset;
}

/** Status of an asset's derived files, in words ("Ready", "Uploading"…). */
export function assetStatus(a: Asset) {
  switch (a.status) {
    case 'uploading':
      return 'Uploading';
    case 'processing':
      return 'Processing';
    case 'error':
      return 'Failed';
    case 'cold':
      return 'Archived (editing copy rebuilt on use)';
    default:
      return 'Ready';
  }
}

/**
 * Details of the asset library's selection, or of the folder or library being
 * browsed when nothing is selected.
 */
export function AssetsInspector({
  selected,
  index,
  folderById,
  folderStats,
  libraryName,
  scopeAssets,
  scopeName,
  folderId,
  sp,
  progress,
  reviews,
  onShare,
}: {
  selected: string[];
  index: MediaIndex;
  folderById: Map<string, AssetFolder>;
  folderStats: (id: string) => FolderStats;
  libraryName: (x: { projectId: string; spaceId: string }) => string;
  /** Files of the library being browsed (when nothing is selected at its top). */
  scopeAssets: readonly Asset[];
  scopeName: string;
  folderId: string;
  sp: Spaces;
  progress: (a: Asset) => number | undefined;
  /** Review status of assets shared for review, by asset id. */
  reviews?: ReadonlyMap<string, AssetReview>;
  /** Opens a file's share dialog (its review links and approvals). */
  onShare?: (a: Asset, tab?: 'links' | 'overview') => void;
}) {
  const grantsOf = (id: string) => sp.grants.filter((g) => g.targetId === id).length;
  const pathOf = (x: { projectId: string; spaceId: string; folderId?: string; parentId?: string }) => {
    const names: string[] = [];
    for (let f = folderById.get(x.folderId ?? x.parentId ?? ''), i = 0; f && i < 64; f = folderById.get(f.parentId), i++) names.unshift(f.name);
    return [libraryName(x), ...names].join(' / ');
  };

  if (selected.length > 1) {
    const files = selected.filter((k) => k.startsWith('a:')).map((k) => index.byId.get(k.slice(2))).filter((a): a is Asset => !!a);
    const dirs = selected.filter((k) => k.startsWith('f:')).map((k) => folderStats(k.slice(2)));
    const size = files.reduce((s, a) => s + a.sizeBytes, 0) + dirs.reduce((s, d) => s + d.size, 0);
    const storage = files.reduce((s, a) => s + a.totalBytes, 0) + dirs.reduce((s, d) => s + d.storage, 0);
    const duration = files.reduce((s, a) => s + a.durationUs, 0);
    return (
      <div data-inspector-multi>
        <InfoTitle
          icon={<Layers size={18} className="text-accent-2" />}
          title={`${plural(selected.length, 'item')} selected`}
          subtitle={[files.length ? plural(files.length, 'file') : '', dirs.length ? plural(dirs.length, 'folder') : ''].filter(Boolean).join(', ')}
        />
        <InfoSection title="Together">
          <InfoRow label="Files (incl. in folders)">{files.length + dirs.reduce((s, d) => s + d.files, 0)}</InfoRow>
          {duration ? <InfoRow label="Total length">{formatDuration(duration)}</InfoRow> : null}
          <InfoRow label="Total size">{formatBytes(size)}</InfoRow>
          <InfoRow label="With proxies">{formatBytes(storage)}</InfoRow>
        </InfoSection>
      </div>
    );
  }

  const key = selected[0];
  const asset = key?.startsWith('a:') ? index.byId.get(key.slice(2)) : undefined;
  if (asset) {
    const users = index.usedBy.get(asset.id) ?? [];
    const bitrate = averageBitrate(asset.sizeBytes, asset.durationUs);
    const media = asset.kind === 'video' || asset.kind === 'audio';
    const review = reviews?.get(asset.id);
    const reviewLabel = review ? REVIEW_LABEL[review.status] : undefined;
    return (
      <div data-inspector-asset={asset.id}>
        <AssetThumb asset={asset} progress={progress(asset)} className="mb-3 rounded-lg" />
        <InfoTitle title={asset.name} subtitle={pathOf(asset)} />
        {reviewLabel ? (
          <div className="mb-3 flex flex-wrap gap-1.5" data-inspector-asset-review={review!.status}>
            <Badge tone={reviewLabel.tone}>{reviewLabel.label}</Badge>
          </div>
        ) : null}
        <InfoSection title="File">
          <InfoRow label="Type">{kindLabel(asset)}</InfoRow>
          <InfoRow label="Size">{formatBytes(asset.sizeBytes)}</InfoRow>
          <InfoRow label="With proxies">{formatBytes(asset.totalBytes)}</InfoRow>
          {asset.width ? (
            <InfoRow label="Resolution">
              {asset.width}×{asset.height} ({aspectLabel(asset.width, asset.height)})
            </InfoRow>
          ) : null}
          {asset.fps ? <InfoRow label="Frame rate">{Math.round(asset.fps * 1000) / 1000} fps</InfoRow> : null}
          {asset.durationUs ? <InfoRow label="Length">{formatDuration(asset.durationUs)}</InfoRow> : null}
          {asset.videoCodec ? <InfoRow label="Video codec">{asset.videoCodec}</InfoRow> : null}
          {asset.audioCodec ? (
            <InfoRow label="Audio">
              {asset.audioCodec}
              {asset.audioChannels ? ` · ${asset.audioChannels === 1 ? 'mono' : asset.audioChannels === 2 ? 'stereo' : `${asset.audioChannels} ch`}` : ''}
              {asset.sampleRate ? ` · ${(asset.sampleRate / 1000).toFixed(1)} kHz` : ''}
            </InfoRow>
          ) : null}
          {media && bitrate ? <InfoRow label="Bitrate (avg.)">{formatBitrate(bitrate)}</InfoRow> : null}
        </InfoSection>
        {asset.sourceId ? <LinkedOrigin asset={asset} /> : null}
        <InfoSection title="Status">
          <InfoRow label="State">{asset.status === 'error' ? <span className="text-danger" title={asset.error}>Failed</span> : assetStatus(asset)}</InfoRow>
          {asset.hasVideo && asset.kind !== 'image' ? <InfoRow label="Editing proxy">{asset.proxyKey ? 'Ready' : asset.status === 'ready' ? 'None' : 'Not yet'}</InfoRow> : null}
          {asset.hasVideo && asset.kind !== 'image' ? <InfoRow label="Filmstrip">{asset.filmstripKey ? 'Ready' : 'Not yet'}</InfoRow> : null}
          {asset.hasAudio ? <InfoRow label="Waveform">{asset.waveformKey ? 'Ready' : 'Not yet'}</InfoRow> : null}
          {asset.status === 'error' && asset.error ? <p className="mt-1 break-words text-xs text-danger">{asset.error}</p> : null}
        </InfoSection>
        <InfoSection title="History">
          <InfoRow label="Uploaded">
            <When at={asset.createdAt} by={asset.createdByName} />
          </InfoRow>
        </InfoSection>
        {review ? (
          <InfoSection title="Review">
            <InfoRow label="Status">{reviewLabel?.label ?? 'Not in review'}</InfoRow>
            {reviewLabel ? <InfoRow label="Round">{review.round}</InfoRow> : null}
            {reviewLabel && review.requestedByName ? <InfoRow label="Started by">{review.requestedByName}</InfoRow> : null}
            <InfoRow label="Last change">
              <When at={review.updatedAt} />
            </InfoRow>
            {onShare ? (
              <span className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                <Link className="text-xs font-medium text-accent-2 hover:underline" to={`/orgs/${asset.orgId}/assets/${asset.id}/review`} data-inspector-review-overview>
                  Review overview
                </Link>
                <button className="text-xs font-medium text-accent-2 hover:underline" onClick={() => onShare(asset)} data-inspector-open-review>
                  Open review and links
                </button>
              </span>
            ) : null}
          </InfoSection>
        ) : null}
        <InfoSection title="Used in">
          {users.length ? (
            <ul className="space-y-1 text-xs">
              {users.slice(0, 12).map((p) => (
                <li key={p.id}>
                  <Link to={`/p/${p.id}`} className="flex items-center gap-1.5 truncate text-fg hover:text-accent-2">
                    <Clapperboard size={12} className="shrink-0 text-faint" />
                    <span className="truncate">{p.name}</span>
                  </Link>
                </li>
              ))}
              {users.length > 12 ? <li className="text-faint">and {users.length - 12} more</li> : null}
            </ul>
          ) : (
            <p className="text-xs text-faint">Not in any project you can see.</p>
          )}
        </InfoSection>
      </div>
    );
  }

  const folder = key?.startsWith('f:') ? folderById.get(key.slice(2)) : !key && folderId ? folderById.get(folderId) : undefined;
  if (folder) {
    const s = folderStats(folder.id);
    return (
      <div data-inspector-folder={folder.id}>
        <InfoTitle icon={<Folder size={18} className="text-accent-2" />} title={folder.name} subtitle={`Folder in ${pathOf(folder)}`} />
        <InfoSection title="Contents">
          <InfoRow label="Files">{s.files}</InfoRow>
          <InfoRow label="Subfolders">{s.folders}</InfoRow>
          <InfoRow label="Total size">{formatBytes(s.size)}</InfoRow>
          <InfoRow label="With proxies">{formatBytes(s.storage)}</InfoRow>
        </InfoSection>
        <InfoSection title="History">
          <InfoRow label="Created">
            <When at={folder.createdAt} by={folder.createdByName} />
          </InfoRow>
          <InfoRow label="Last added">{s.latest ? <When at={s.latest.createdAt} by={s.latest.createdByName} /> : null}</InfoRow>
        </InfoSection>
        <InfoSection title="Access">
          <InfoRow label="Who can see it">{accessText(folder.restricted, folder.scopeId, grantsOf(folder.id))}</InfoRow>
        </InfoSection>
      </div>
    );
  }

  const latest = latestBy(scopeAssets, (a) => micros(a.createdAt));
  const size = scopeAssets.reduce((s, a) => s + a.sizeBytes, 0);
  const storage = scopeAssets.reduce((s, a) => s + a.totalBytes, 0);
  const byKind = ['video', 'audio', 'image', 'font', 'lut'].map((k) => [k, scopeAssets.filter((a) => a.kind === k).length] as const).filter(([, n]) => n);
  return (
    <div data-inspector-library>
      <InfoTitle icon={<FolderOpen size={18} className="text-accent-2" />} title={scopeName} subtitle="Media library" />
      <InfoSection title="Contents">
        <InfoRow label="Files">{scopeAssets.length}</InfoRow>
        {byKind.map(([k, n]) => (
          <InfoRow key={k} label={`· ${KIND_LABEL[k]}`}>
            {n}
          </InfoRow>
        ))}
        <InfoRow label="Total size">{formatBytes(size)}</InfoRow>
        <InfoRow label="With proxies">{formatBytes(storage)}</InfoRow>
      </InfoSection>
      <InfoSection title="Activity">
        <InfoRow label="Last added">{latest ? <When at={latest.createdAt} by={latest.createdByName} /> : null}</InfoRow>
      </InfoSection>
      <p className="mt-2 text-xs text-faint">Select a file or folder to see its details.</p>
    </div>
  );
}

/** Media linked in place: where the original is kept (for a NAS, the worker that shares it and runs its exports). */
function LinkedOrigin({ asset }: { asset: Asset }) {
  const o = useLinkedOrigin(asset);
  if (!o) return null;
  return (
    <InfoSection title="Original">
      <InfoRow label="Linked from">
        <span data-inspector-linked={o.nas ? 'nas' : 'bucket'}>
          {o.connection ?? 'a storage connection'}
          {o.nas ? ` on ${o.worker ?? 'its worker'}` : ''}
        </span>
      </InfoRow>
      {o.path ? <InfoRow label="Path">{o.path}</InfoRow> : null}
      {asset.sourceMissing ? <p className="mt-1 text-xs text-danger">The original can no longer be found there (removed, moved or renamed). Its preview still plays; exports need the original.</p> : null}
      {o.nas ? (
        <p className="mt-1 text-xs text-faint" data-inspector-linked-note>
          The original stays on the NAS; only its preview is in vidmakr. Exports and conversions using it run on {o.worker ?? 'the worker that shares the folder'}
          {o.worker && o.workerOnline === false ? ' (offline right now: they wait for it)' : ''}.
        </p>
      ) : null}
    </InfoSection>
  );
}
